import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const FAQS = [
  ["Is there a free trial?", "Yes. Every new company gets a 14-day free trial with full access. You don't need a card to sign up."],
  ["What happens when my trial ends?", "Choose a plan on the Billing page at any time. If the trial ends without a plan, your account is paused until you subscribe. Nothing is deleted, so you pick up exactly where you left off."],
  ["Can I cancel at any time?", "Yes. Plans are monthly with no long contract. You can cancel or change your card from Manage billing on the Billing page."],
  ["Does my team need to download an app?", "No. Warrant Whizz works in the browser on any phone, tablet or computer. Team members get an invite, set a password and sign in."],
  ["Can team members see work assigned to other people?", "No. Team members see work assigned to them. Managers can see the whole company team and its work."],
  ["Can I choose the types of people on my team?", "Yes. Choose Locksmiths, Warrant Officers, Engineers and Dog Handlers during signup, then update the enabled types in Company settings."],
  ["Can I manage pre-warrant visits?", "Yes. Managers create visits, choose a company Visit Type and assign a Warrant Officer. The assigned officer can update the visit status and add photos."],
  ["Can Warrant Officers see every visit?", "No. Managers can see all company visits. A Warrant Officer sees only visits assigned to them."],
  ["Can a team member work for more than one company?", "Yes. A person can belong to several companies with one login and switch between them. Each company's records stay completely separate."],
  ["Is our data kept private?", "Yes. Every company has its own private workspace. No other company can see your runs, addresses, lock changes or key safe codes."],
  ["Can I import addresses I already have?", "Yes. Upload a CSV or Excel file and the addresses are added to your run, with any rows that need fixing clearly flagged."],
  ["Can I get my data out?", "Yes. Export any run as a PDF or CSV, or a whole date range as one Excel spreadsheet. PDFs can show your company logo."],
  ["How do I get help?", "Email michael@newhamemergencylocksmiths.co.uk and we'll get back to you."],
];

const useFaqSchema = () => {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-schema";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);
};

export const Faq = () => {
  useFaqSchema();
  return (
    <section id="questions" className="mx-auto grid max-w-6xl gap-12 px-6 py-24 lg:grid-cols-[1fr_1.6fr]" data-testid="home-faq">
      <div>
        <p className="font-data text-xs uppercase tracking-[0.2em] text-blue-400">Questions</p>
        <h2 className="font-display mt-2 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Common questions</h2>
        <p className="mt-3 text-slate-300">Everything people usually ask before they sign up. Can&apos;t see yours?{" "}
          <a href="mailto:michael@newhamemergencylocksmiths.co.uk" className="font-medium text-blue-400 hover:underline" data-testid="faq-email-link">Email us</a>.</p>
      </div>
      <Accordion type="single" collapsible className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white px-6 shadow-xl" data-testid="faq-list">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="border-0">
            <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-slate-900 hover:text-blue-700 hover:no-underline" data-testid={`faq-question-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};
